import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Card, Table, Spinner, Alert, Accordion } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../styles/Reports.css';
import { API_BASE_URL } from '../utils/apiConfig';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';
import { generateOutstandingInvoicesReportPDF } from '../utils/reportPdfGenerator';

const Reports = () => {
  const currentYear = new Date().getFullYear();
  const [reportStates, setReportStates] = useState({
    'sales-by-product': { data: null, loading: false, error: null },
    'sales-by-customer': { data: null, loading: false, error: null },
    'sales-by-current-month': { data: null, loading: false, error: null },
    'sales-by-current-year': { data: null, loading: false, error: null },
    'outstanding-invoices': { data: null, loading: false, error: null },
    'payment-status-summary': { data: null, loading: false, error: null },
    'monthly-comparison': { data: null, loading: false, error: null },
    'monthly-sales-gst': { data: null, loading: false, error: null }
  });
  const [outstandingPeriodValue, setOutstandingPeriodValue] = useState(`year-${currentYear}`);
  const [selectedGstYear, setSelectedGstYear] = useState(null);
  const [selectedGstMonth, setSelectedGstMonth] = useState(null);
  const [gstCsvDownloading, setGstCsvDownloading] = useState(false);
  const [gstCsvError, setGstCsvError] = useState(null);

  const reports = [
    {
      id: 'sales-by-product',
      title: 'Sales by Product',
      description: 'Revenue breakdown by product',
      icon: '📦'
    },
    {
      id: 'sales-by-customer',
      title: 'Sales by Customer',
      description: 'Revenue breakdown by customer',
      icon: '👥'
    },
    {
      id: 'sales-by-current-month',
      title: 'Sales by Current Month',
      description: 'Sales summary for this month',
      icon: '📅'
    },
    {
      id: 'sales-by-current-year',
      title: 'Sales by Current Year',
      description: 'Sales summary for this year',
      icon: '📊'
    },
    {
      id: 'outstanding-invoices',
      title: 'Outstanding Invoices',
      description: 'Unpaid and pending invoices by customer',
      icon: '💰'
    },
    {
      id: 'payment-status-summary',
      title: 'Payment Status Summary',
      description: 'Paid vs Unpaid invoices breakdown',
      icon: '💳'
    },
    {
      id: 'monthly-comparison',
      title: 'Monthly Comparison',
      description: 'Compare current month vs previous month',
      icon: '📈'
    },
    {
      id: 'monthly-sales-gst',
      title: 'Monthly Sales GST Report',
      description: 'Download a CSV GST breakdown for a selected month',
      icon: '🧾'
    }
  ];

  const parseOutstandingPeriodValue = (periodValue) => {
    const parts = String(periodValue || '').split('-');
    if (parts[0] === 'month' && parts.length === 3) {
      const selectedYear = Number(parts[1]);
      const selectedMonth = Number(parts[2]);
      if (Number.isInteger(selectedYear) && Number.isInteger(selectedMonth)) {
        return {
          periodType: 'month',
          selectedYear,
          selectedMonth
        };
      }
    }

    const selectedYear = Number(parts[1]);
    return {
      periodType: 'year',
      selectedYear: Number.isInteger(selectedYear) ? selectedYear : currentYear
    };
  };

  const getOutstandingPeriodOptions = (data) => {
    const years = Array.isArray(data?.filters?.years) ? data.filters.years : [];
    const monthsByYear = data?.filters?.monthsByYear || {};
    const options = [];

    years.forEach((yearOption) => {
      const year = Number(yearOption.value);
      if (!Number.isInteger(year)) {
        return;
      }

      options.push({
        value: `year-${year}`,
        label: yearOption.label || String(year)
      });

      const months = Array.isArray(monthsByYear[String(year)]) ? monthsByYear[String(year)] : [];
      months.forEach((monthOption) => {
        const month = Number(monthOption.value);
        if (!Number.isInteger(month)) {
          return;
        }

        const monthLabel = monthOption.label || `Month ${month}`;
        options.push({
          value: `month-${year}-${month}`,
          label: `${monthLabel} ${year}`
        });
      });
    });

    return options;
  };

  const fetchReportData = async (reportId, options = {}) => {
    const { force = false, params = {} } = options;
    const currentState = reportStates[reportId];

    if (!force && currentState.data) {
      return;
    }

    setReportStates(prev => ({
      ...prev,
      [reportId]: { ...prev[reportId], loading: true, error: null }
    }));

    try {
      const url = reportId === 'monthly-sales-gst'
        ? `${API_BASE_URL}/reports/monthly-sales-gst/filters`
        : `${API_BASE_URL}/reports/${reportId}`;
      const response = await axios.get(url, { params });
      setReportStates(prev => ({
        ...prev,
        [reportId]: { data: response.data, loading: false, error: null }
      }));
    } catch (err) {
      console.error('Error fetching report:', err);
      const errorMsg = err.response?.data?.error || 'Failed to load report data';
      setReportStates(prev => ({
        ...prev,
        [reportId]: { data: null, loading: false, error: errorMsg }
      }));
    }
  };

  const handleReportToggle = async (reportId) => {
    if (reportId === 'outstanding-invoices') {
      const params = parseOutstandingPeriodValue(outstandingPeriodValue);
      await fetchReportData(reportId, { params });
      return;
    }

    await fetchReportData(reportId);
  };

  const handleOutstandingPeriodChange = async (nextPeriodValue) => {
    const periodParams = parseOutstandingPeriodValue(nextPeriodValue);
    setOutstandingPeriodValue(nextPeriodValue);
    await fetchReportData('outstanding-invoices', {
      force: true,
      params: periodParams
    });
  };

  const formatCurrency = (value) => {
    return formatCurrencyBySystem(
      Number(value || 0),
      getNumberingSystemFromStorage(),
      { prefix: getCurrencyPrefixFromStorage(), minimumFractionDigits: 0, maximumFractionDigits: 0 }
    );
  };

  const handleGstYearChange = (nextYear) => {
    const year = Number(nextYear);
    setSelectedGstYear(year);
    const data = reportStates['monthly-sales-gst']?.data;
    const monthsForYear = Array.isArray(data?.monthsByYear?.[String(year)]) ? data.monthsByYear[String(year)] : [];
    setSelectedGstMonth(monthsForYear.length > 0 ? monthsForYear[0].value : null);
  };

  const parseCsvErrorBlob = async (blob) => {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return parsed?.error || null;
    } catch (parseErr) {
      return null;
    }
  };

  const handleGenerateGstCsv = async () => {
    if (!selectedGstYear || !selectedGstMonth) {
      return;
    }

    setGstCsvDownloading(true);
    setGstCsvError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/reports/monthly-sales-gst/csv`, {
        params: { year: selectedGstYear, month: selectedGstMonth },
        responseType: 'blob'
      });

      const disposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^";]+)"?/);
      const fileName = fileNameMatch?.[1] || `Monthly-Sales-GST-Report-${selectedGstYear}-${selectedGstMonth}.csv`;

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      let errorMsg = 'Failed to generate report';
      if (err.response?.data instanceof Blob) {
        errorMsg = (await parseCsvErrorBlob(err.response.data)) || errorMsg;
      } else {
        errorMsg = err.response?.data?.error || errorMsg;
      }
      setGstCsvError(errorMsg);
    } finally {
      setGstCsvDownloading(false);
    }
  };

  useEffect(() => {
    const data = reportStates['outstanding-invoices']?.data;
    if (!data) {
      return;
    }

    const periodOptions = getOutstandingPeriodOptions(data);
    if (periodOptions.length === 0) {
      return;
    }

    const optionExists = periodOptions.some(option => option.value === outstandingPeriodValue);
    if (!optionExists) {
      setOutstandingPeriodValue(periodOptions[0].value);
    }
  }, [reportStates, outstandingPeriodValue]);

  useEffect(() => {
    const data = reportStates['monthly-sales-gst']?.data;
    const years = Array.isArray(data?.years) ? data.years : [];
    if (years.length === 0) {
      return;
    }

    setSelectedGstYear((prevYear) => {
      const year = prevYear !== null && years.some((item) => item.value === prevYear)
        ? prevYear
        : years[0].value;

      const monthsForYear = Array.isArray(data?.monthsByYear?.[String(year)]) ? data.monthsByYear[String(year)] : [];
      setSelectedGstMonth((prevMonth) => {
        if (monthsForYear.length === 0) {
          return null;
        }
        return prevMonth !== null && monthsForYear.some((item) => item.value === prevMonth)
          ? prevMonth
          : monthsForYear[0].value;
      });

      return year;
    });
  }, [reportStates]);

  const renderSalesByProductTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const point = payload[0]?.payload || {};
    const description = String(point.productDescription || '').trim();

    return (
      <div className="bg-white border rounded p-2 shadow-sm">
        <div><strong>{label}</strong></div>
        {description ? <div>{description}</div> : null}
        <div>{`Total Sales: ${formatCurrency(payload[0].value)}`}</div>
      </div>
    );
  };

  const renderProductNameCell = (item) => {
    const productName = String(item?.productName || 'Unknown Product').trim() || 'Unknown Product';
    const description = String(item?.productDescription || '').trim();
    const hoverText = description || 'No description available';

    return (
      <td title={hoverText}>
        {productName}
      </td>
    );
  };

  const renderSalesByProduct = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <p className="text-muted">No data available</p>;
    }

    return (
      <>
        <div className="my-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip content={renderSalesByProductTooltip} />
              <Legend />
              <Bar dataKey="totalAmount" fill="#8884d8" name="Total Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Product Name</th>
                <th className="text-end">Total Sales</th>
                <th className="text-end">Units Sold</th>
                <th className="text-end">Average Price</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx}>
                  {renderProductNameCell(item)}
                  <td className="text-end">{formatCurrency(item.totalAmount)}</td>
                  <td className="text-end">{item.unitsSold}</td>
                  <td className="text-end">{formatCurrency(item.averagePrice)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  const renderSalesByCustomer = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <p className="text-muted">No data available</p>;
    }

    return (
      <>
        <div className="my-4">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="customerName" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="totalAmount" fill="#82ca9d" name="Total Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Customer Name</th>
                <th className="text-end">Total Sales</th>
                <th className="text-end">Invoice Count</th>
                <th>Last Invoice Date</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.customerName}</td>
                  <td className="text-end">{formatCurrency(item.totalAmount)}</td>
                  <td className="text-end">{item.invoiceCount}</td>
                  <td>{item.lastInvoiceDate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  const renderSalesByCurrentMonth = (data) => {
    if (!data || !data.summary) {
      return <p className="text-muted">No data available</p>;
    }

    const { summary, topCustomers, topProducts } = data;

    return (
      <>
        <div className="my-4">
          <div className="row">
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Sales</h6>
                  <h3>{formatCurrency(summary.totalSalesAmount)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Invoices</h6>
                  <h3>{summary.totalInvoices}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Period</h6>
                  <h3 style={{ fontSize: '1.2rem' }}>{summary.month}</h3>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>

        {topCustomers && topCustomers.length > 0 && (
          <>
            <h5 className="mt-4">Top Customers</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="text-end">Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.customerName}</td>
                      <td className="text-end">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {topProducts && topProducts.length > 0 && (
          <>
            <h5 className="mt-4">Top Products</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Sales</th>
                    <th className="text-end">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((item, idx) => (
                    <tr key={idx} title={String(item.productDescription || '').trim() || 'No description available'}>
                      {renderProductNameCell(item)}
                      <td className="text-end">{formatCurrency(item.amount)}</td>
                      <td className="text-end">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </>
    );
  };

  const renderSalesByCurrentYear = (data) => {
    if (!data || !data.summary) {
      return <p className="text-muted">No data available</p>;
    }

    const { summary, monthlyBreakdown, topCustomers, topProducts } = data;

    return (
      <>
        <div className="my-4">
          <div className="row">
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Sales</h6>
                  <h3>{formatCurrency(summary.totalSalesAmount)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Invoices</h6>
                  <h3>{summary.totalInvoices}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Year</h6>
                  <h3>{summary.year}</h3>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>

        {monthlyBreakdown && Object.keys(monthlyBreakdown).length > 0 && (
          <>
            <h5 className="mt-4">Monthly Breakdown</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Sales</th>
                    <th className="text-end">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(monthlyBreakdown).map(([month, data], idx) => (
                    <tr key={idx}>
                      <td>{month}</td>
                      <td className="text-end">{formatCurrency(data.amount)}</td>
                      <td className="text-end">{data.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {topCustomers && topCustomers.length > 0 && (
          <>
            <h5 className="mt-4">Top Customers</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="text-end">Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.customerName}</td>
                      <td className="text-end">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {topProducts && topProducts.length > 0 && (
          <>
            <h5 className="mt-4">Top Products</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Sales</th>
                    <th className="text-end">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((item, idx) => (
                    <tr key={idx} title={String(item.productDescription || '').trim() || 'No description available'}>
                      {renderProductNameCell(item)}
                      <td className="text-end">{formatCurrency(item.amount)}</td>
                      <td className="text-end">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </>
    );
  };

  const renderOutstandingInvoices = (data) => {
    if (!data || !data.summary) {
      return <p className="text-muted">No outstanding invoices</p>;
    }

    const { summary, period, invoices, customerSubtotals } = data;
    const periodOptions = getOutstandingPeriodOptions(data);

    const exportOutstandingReport = () => {
      generateOutstandingInvoicesReportPDF(data, formatCurrency);
    };

    return (
      <>
        <div className="outstanding-toolbar">
          <div className="outstanding-toolbar-label-wrap">
            <label htmlFor="outstandingPeriod" className="outstanding-toolbar-label">Time period</label>
            <select
              id="outstandingPeriod"
              className="outstanding-period-select"
              value={outstandingPeriodValue}
              onChange={(event) => handleOutstandingPeriodChange(event.target.value)}
              disabled={periodOptions.length === 0}
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="outstanding-export-btn"
            onClick={exportOutstandingReport}
            disabled={!Array.isArray(invoices) || invoices.length === 0}
            title="Export outstanding invoices report as PDF"
          >
            PDF
          </button>
        </div>

        <div className="my-4">
          <div className="row">
            <div className="col-md-4 mb-3">
              <Card className="bg-danger bg-opacity-10">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Outstanding</h6>
                  <h3 className="text-danger">{formatCurrency(summary.totalOutstanding)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Invoice Count</h6>
                  <h3>{summary.totalInvoices}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Period</h6>
                  <h3 className="outstanding-period-title">{period?.label || 'Current Year'}</h3>
                </Card.Body>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Invoice Amount</h6>
                  <h3>{formatCurrency(summary.totalAmount)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Received</h6>
                  <h3 className="text-success">{formatCurrency(summary.totalReceived)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Tax</h6>
                  <h3>{formatCurrency(summary.totalTax)}</h3>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>

        {invoices && invoices.length > 0 && (
          <>
            <h5 className="mt-2">Pending Invoice Details</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Customer Name</th>
                    <th>Date</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Tax</th>
                    <th className="text-end">Received</th>
                    <th className="text-end">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.invoiceNumber}</td>
                      <td>{item.customerName}</td>
                      <td>{item.date}</td>
                      <td className="text-end">{formatCurrency(item.amount)}</td>
                      <td className="text-end">{formatCurrency(item.tax)}</td>
                      <td className="text-end text-success">{formatCurrency(item.received)}</td>
                      <td className="text-end text-danger">{formatCurrency(item.pending)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {customerSubtotals && customerSubtotals.length > 0 && (
          <>
            <h5 className="mt-4">Customer Subtotals</h5>
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="text-end">Invoice Count</th>
                    <th className="text-end">Pending Total</th>
                  </tr>
                </thead>
                <tbody>
                  {customerSubtotals.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.customerName}</td>
                      <td className="text-end">{item.invoiceCount}</td>
                      <td className="text-end text-danger">{formatCurrency(item.pendingAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {(!invoices || invoices.length === 0) && (
          <div className="table-responsive">
            <p className="text-muted mb-0">No pending invoices for the selected period.</p>
          </div>
        )}
      </>
    );
  };

  const renderPaymentStatusSummary = (data) => {
    if (!data || !data.summary) {
      return <p className="text-muted">No data available</p>;
    }

    const { summary, paid, unpaid } = data;

    return (
      <>
        <div className="my-4">
          <div className="row">
            <div className="col-md-3 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Invoices</h6>
                  <h3>{summary.totalInvoices}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3 mb-3">
              <Card className="bg-light">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Total Amount</h6>
                  <h3>{formatCurrency(summary.totalAmount)}</h3>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3 mb-3">
              <Card className="bg-success bg-opacity-10">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Paid</h6>
                  <h4 className="text-success">{paid.percentage}%</h4>
                  <p className="mb-0">{formatCurrency(paid.amount)}</p>
                  <small>{paid.count} invoices</small>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-3 mb-3">
              <Card className="bg-warning bg-opacity-10">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Unpaid</h6>
                  <h4 className="text-warning">{unpaid.percentage}%</h4>
                  <p className="mb-0">{formatCurrency(unpaid.amount)}</p>
                  <small>{unpaid.count} invoices</small>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMonthlyComparison = (data) => {
    if (!data || !data.currentMonth) {
      return <p className="text-muted">No data available</p>;
    }

    const { currentMonth, previousMonth, comparison } = data;
    const isGrowth = comparison.salesGrowth >= 0;

    return (
      <>
        <div className="my-4">
          <div className="row">
            <div className="col-md-6 mb-3">
              <Card className="bg-light">
                <Card.Body>
                  <h6 className="text-muted mb-3">{currentMonth.month}</h6>
                  <div className="mb-3">
                    <small className="text-muted">Total Sales</small>
                    <h4>{formatCurrency(currentMonth.totalSales)}</h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Invoice Count</small>
                    <h5>{currentMonth.invoiceCount}</h5>
                  </div>
                  <div>
                    <small className="text-muted">Average per Invoice</small>
                    <h5>{formatCurrency(currentMonth.averagePerInvoice)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-6 mb-3">
              <Card className="bg-light">
                <Card.Body>
                  <h6 className="text-muted mb-3">{previousMonth.month}</h6>
                  <div className="mb-3">
                    <small className="text-muted">Total Sales</small>
                    <h4>{formatCurrency(previousMonth.totalSales)}</h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Invoice Count</small>
                    <h5>{previousMonth.invoiceCount}</h5>
                  </div>
                  <div>
                    <small className="text-muted">Average per Invoice</small>
                    <h5>{formatCurrency(previousMonth.averagePerInvoice)}</h5>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>

          <Card className="mt-4">
            <Card.Body>
              <h5 className="mb-4">Comparison & Growth</h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <h6>Sales Growth</h6>
                    <div className={`display-6 ${isGrowth ? 'text-success' : 'text-danger'}`}>
                      {isGrowth ? '+' : ''}{comparison.salesGrowth}%
                    </div>
                    <small className="text-muted">
                      {isGrowth ? '+' : ''}{formatCurrency(comparison.salesGrowthAmount)}
                    </small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <h6>Invoice Growth</h6>
                    <div className={`display-6 ${comparison.invoiceGrowth >= 0 ? 'text-success' : 'text-danger'}`}>
                      {comparison.invoiceGrowth >= 0 ? '+' : ''}{comparison.invoiceGrowth}%
                    </div>
                    <small className="text-muted">
                      {comparison.invoiceGrowth >= 0 ? '+' : ''}{comparison.invoiceGrowthAmount} invoices
                    </small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </>
    );
  };

  const renderMonthlySalesGst = (data) => {
    const years = Array.isArray(data?.years) ? data.years : [];
    const monthsForYear = years.length > 0
      ? (Array.isArray(data?.monthsByYear?.[String(selectedGstYear)]) ? data.monthsByYear[String(selectedGstYear)] : [])
      : [];

    return (
      <>
        <p className="text-muted">
          Select a year and month, then click Generate to download a CSV GST breakdown of that month's invoices.
        </p>

        {years.length === 0 ? (
          <p className="text-muted mb-0">No invoice data available yet.</p>
        ) : (
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
              <label htmlFor="gstYearSelect" className="form-label mb-1">Year</label>
              <select
                id="gstYearSelect"
                className="form-select"
                value={selectedGstYear ?? ''}
                onChange={(event) => handleGstYearChange(event.target.value)}
              >
                {years.map((year) => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="gstMonthSelect" className="form-label mb-1">Month</label>
              <select
                id="gstMonthSelect"
                className="form-select"
                value={selectedGstMonth ?? ''}
                onChange={(event) => setSelectedGstMonth(Number(event.target.value))}
                disabled={monthsForYear.length === 0}
              >
                {monthsForYear.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerateGstCsv}
              disabled={gstCsvDownloading || !selectedGstYear || !selectedGstMonth}
            >
              {gstCsvDownloading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        )}

        {gstCsvError && <Alert variant="danger">{gstCsvError}</Alert>}
      </>
    );
  };

  const renderReportContent = (reportId, data) => {
    switch (reportId) {
      case 'sales-by-product':
        return renderSalesByProduct(data);
      case 'sales-by-customer':
        return renderSalesByCustomer(data);
      case 'sales-by-current-month':
        return renderSalesByCurrentMonth(data);
      case 'sales-by-current-year':
        return renderSalesByCurrentYear(data);
      case 'outstanding-invoices':
        return renderOutstandingInvoices(data);
      case 'payment-status-summary':
        return renderPaymentStatusSummary(data);
      case 'monthly-comparison':
        return renderMonthlyComparison(data);
      case 'monthly-sales-gst':
        return renderMonthlySalesGst(data);
      default:
        return null;
    }
  };
  return (
    <Container className="py-4">
      <h1 className="mb-4">📊 Reports</h1>

      <Accordion>
        {reports.map((report) => {
          const state = reportStates[report.id];

          return (
            <Accordion.Item eventKey={report.id} key={report.id}>
              <Accordion.Header onClick={() => handleReportToggle(report.id)}>
                <span style={{ fontSize: '1.5rem' }} className="me-3">
                  {report.icon}
                </span>
                <div>
                  <strong>{report.title}</strong>
                  <br />
                  <small className="text-muted">{report.description}</small>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {state.loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="me-2" />
                    Loading report...
                  </div>
                )}

                {state.error && <Alert variant="danger">{state.error}</Alert>}

                {state.data && !state.loading && renderReportContent(report.id, state.data)}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Container>
  );
};

export default Reports;
